// =============================================================================
// pages/SilentCustomers.js — Pasif Müşteri Uyandırma (Faz BI)
// =============================================================================
// Owner için en değerli liste: B2B'de uzun süredir sipariş vermeyen müşteriler.
// Tek tık WhatsApp / telefon. Risk seviyesine göre renkli sıralı.
// =============================================================================

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, ToggleButton,
  ToggleButtonGroup, CircularProgress, Stack, Alert, Avatar, Card, CardContent
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";

const API_BASE = "https://api.miksanplastik.com.tr";

function fmt(n) { return '₺' + (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'; }

// Türk telefon numarasını WhatsApp formatına dönüştür (90 prefix)
function waLink(phone) {
  if (!phone) return null;
  const clean = String(phone).replace(/\D/g, '');
  // 0 ile başlıyorsa 90 ile değiştir, 5 ile başlıyorsa 90 ekle, 90 ile başlıyorsa olduğu gibi
  let formatted = clean;
  if (clean.startsWith('0')) formatted = '90' + clean.slice(1);
  else if (clean.startsWith('5') && clean.length === 10) formatted = '90' + clean;
  else if (!clean.startsWith('90')) formatted = '90' + clean;
  return `https://wa.me/${formatted}`;
}

const URGENCY_COLOR = {
  error: 'error', warning: 'warning', info: 'info'
};

export default function SilentCustomers() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(60);
  const [minOrders, setMinOrders] = useState(2);

  const load = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/admin/insights/silent-customers?days=${days}&min_orders=${minOrders}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
      .then(setData)
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [days, minOrders]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={800}>📞 Sessiz Müşteriler</Typography>
          <Typography variant="caption" color="text.secondary">
            Uzun süredir sipariş vermeyen B2B müşteriler — bir telefon onları geri kazanabilir
          </Typography>
        </Box>
        <Tooltip title="Yenile">
          <IconButton onClick={load} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Filtre toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Kaç gündür sessiz?
            </Typography>
            <ToggleButtonGroup size="small" value={days} exclusive onChange={(_, v) => v && setDays(v)}>
              <ToggleButton value={30}>30 gün</ToggleButton>
              <ToggleButton value={60}>60 gün</ToggleButton>
              <ToggleButton value={90}>90 gün</ToggleButton>
              <ToggleButton value={180}>180 gün</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Minimum geçmiş sipariş
            </Typography>
            <ToggleButtonGroup size="small" value={minOrders} exclusive onChange={(_, v) => v && setMinOrders(v)}>
              <ToggleButton value={1}>≥1</ToggleButton>
              <ToggleButton value={2}>≥2</ToggleButton>
              <ToggleButton value={5}>≥5</ToggleButton>
              <ToggleButton value={10}>≥10</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Stack>
      </Paper>

      {/* Özet kartlar */}
      {data && (
        <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
          <SummaryCard label="Toplam müşteri" value={data.total} color="#1565C0" />
          <SummaryCard label="🔥 Kayıp riski" value={data.critical_count} color="#dc2626" />
          <SummaryCard label="⚠️ Acil ara" value={data.warning_count} color="#d97706" />
          <SummaryCard label="Toplam ciro (lifetime)" value={fmt(data.total_lifetime_revenue)} color="#10B981" />
        </Stack>
      )}

      {/* Tablo */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : !data || data.items.length === 0 ? (
        <Alert severity="success">
          🎉 Bu kriterlere uyan sessiz müşteri yok — hepsi yakın zamanda sipariş vermiş!
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Müşteri</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Lifetime</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Sipariş</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Son sipariş</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Kaç gün sessiz</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Ortalama aralık</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Durum</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, width: 180 }}>İletişim</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map(c => (
                <TableRow key={c.id} hover sx={{
                  borderLeft: '4px solid',
                  borderLeftColor: c.urgency === 'error' ? '#dc2626' :
                                   c.urgency === 'warning' ? '#d97706' : '#2563eb'
                }}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ bgcolor: '#1565C0', width: 32, height: 32, fontSize: 14 }}>
                        {(c.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography fontSize={13} fontWeight={600}>
                          {c.company_name || c.name}
                        </Typography>
                        {c.company_name && (
                          <Typography variant="caption" color="text.secondary">{c.name}</Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#1565C0' }}>
                    {fmt(c.lifetime_revenue)}
                  </TableCell>
                  <TableCell align="center">{c.lifetime_orders}</TableCell>
                  <TableCell align="center" sx={{ fontSize: 12 }}>{fmtDate(c.last_confirmed_at)}</TableCell>
                  <TableCell align="center">
                    <Chip label={`${c.days_silent} gün`} size="small"
                          color={c.days_silent > 90 ? 'error' : c.days_silent > 60 ? 'warning' : 'default'} />
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {c.avg_days_between ? `~${Math.round(c.avg_days_between)} gün` : '-'}
                    {c.overdue_ratio && (
                      <Typography variant="caption" display="block" sx={{ fontWeight: 700 }}>
                        {c.overdue_ratio.toFixed(1)}× geç
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={c.urgency_text} size="small" color={URGENCY_COLOR[c.urgency]} />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      {c.phone && (
                        <Tooltip title={`Ara: ${c.phone}`}>
                          <IconButton size="small" component="a" href={`tel:${c.phone}`} sx={{ color: '#1565C0' }}>
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {c.phone && (
                        <Tooltip title="WhatsApp aç">
                          <IconButton size="small" component="a" href={waLink(c.phone)} target="_blank" sx={{ color: '#25D366' }}>
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {c.email && (
                        <Tooltip title={`Email: ${c.email}`}>
                          <IconButton size="small" component="a" href={`mailto:${c.email}`} sx={{ color: '#7C3AED' }}>
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Müşteri Detay">
                        <IconButton size="small" onClick={() => navigate(`/customer/${c.id}`)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <Card sx={{ flex: '1 1 200px', borderTop: `3px solid ${color}` }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={800} sx={{ color }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}
